import { directApiInstance } from './httpClient';

/**
 * SAM API Client
 * 
 * Provides functions to interact with the SAM API endpoints for user and group management.
 * All endpoints enforce tenant isolation based on the JWT token's custom:tenant_id claim.
 */

// Response types matching SAM API
export interface SAMGroup {
  name: string;
  description?: string;
  created: string;
  last_modified: string;
}

export interface SAMUser {
  username: string;
  status: string;
  created: string;
  last_modified: string;
  enabled: boolean;
  email: string;
  name: string;
  family_name: string;
  phone_number?: string;
  tenant_id: string;
  groups: string[];
  /** Cognito sub — this is what the DB stores in `user.cognitoid`. */
  sub?: string;
  /** SAM-801 (#646): numeric `sam_core.user.id` in this tenant; null when the user has no row here. */
  user_id?: number | null;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  name: string;
  family_name: string;
  phone_number?: string;
  group?: string;
  business_id?: string;
  tenant_id?: string;
  custom_attributes?: Record<string, any>;
}

export interface ResetPasswordRequest {
  temporary?: boolean;
  password?: string;
}

export interface ChangePasswordRequest {
  new_password: string;
}

export interface AddUserToGroupRequest {
  group: string;
}

// API Response types
export interface ListGroupsResponse {
  groups: SAMGroup[];
  count: number;
}

export interface ListUsersResponse {
  users: SAMUser[];
  count: number;
  next_token?: string;
}

export interface CreateUserResponse {
  message: string;
  user: {
    username: string;
    tenant_id: string;
    created: string;
  };
  /** SAM-801 (#646): numeric id of the user's row in this tenant; null when it has none yet or the lookup failed. */
  user_id?: number | null;
}

export interface GenericMessageResponse {
  message: string;
}

/**
 * S-60 — the account the operator saw is the account the action must hit. Account actions carry
 * the Cognito `sub` they were opened on as `?expected_sub=`; samUserManagement answers 409
 * `target_mismatch` when the username now belongs to another account (SAM-974). Left out when the
 * account has no sub (an empty value is a 400), and ignored by a backend that does not know it yet.
 */
const expectedSubParams = (expectedSub?: string | null) =>
  expectedSub ? { expected_sub: expectedSub } : undefined;

/**
 * List all Cognito groups that start with "business"
 */
export const listBusinessGroups = async (opts: { quiet?: boolean } = {}) => {
  return await directApiInstance<ListGroupsResponse>({
    url: '/sam/groups',
    method: 'GET',
    // SAM-1320 (U41): the caller shows a failed read itself, so no generic toast on top
    ...(opts.quiet ? { quietErrors: true } : {}),
  } as Parameters<typeof directApiInstance>[0]);
};

/**
 * List users in a specific group, filtered by tenant_id.
 *
 * Pass no `limit` to fetch every tenant user in the group (the backend walks
 * all Cognito pages up to its hard cap). Pass a `limit` only when you really
 * want a capped subset — e.g. typeahead/preview UIs.
 */
export const listGroupUsers = async (
  groupName: string,
  limit?: number,
  token?: string
) => {
  const params: any = {};
  if (limit !== undefined) {
    params.limit = limit;
  }
  if (token) {
    params.token = token;
  }

  return await directApiInstance<ListUsersResponse>({
    url: `/sam/groups/${encodeURIComponent(groupName)}/users`,
    method: 'GET',
    params,
  });
};

/**
 * Fetch ONE Cognito page of users in the caller's tenant.
 *
 * The backend can return at most 60 raw users per call (Cognito hard cap)
 * and filters them by tenant_id and — optionally — by `type`. To get the
 * full tenant set, call this repeatedly with the returned `next_token`
 * until it's absent. The `useAllTenantUsers` hook does this automatically
 * via `useInfiniteQuery`.
 *
 * @param opts.type   'admin' to keep only users with a business_* group,
 *                    'app' to keep users without any business_* group,
 *                    undefined for both.
 * @param opts.token  Cognito PaginationToken from a previous response.
 * @param opts.limit  Max raw users to fetch from Cognito this call (≤ 60).
 * @param opts.businessId  Only the members of that business (`custom:business_id`) in the tenant of the token;
 *                    a business without members answers `users: []` and no `next_token`. A business caller may only
 *                    ask for its own (403), a malformed id is a 400. A backend that does not have the filter yet
 *                    ignores it and answers the whole tenant: callers keep their own filter as a net.
 */
export const listAllTenantUsers = async (
  opts: { type?: 'admin' | 'app'; token?: string; limit?: number; businessId?: string } = {}
) => {
  const params: any = {};
  if (opts.type) params.type = opts.type;
  if (opts.businessId) params.business_id = opts.businessId;
  if (opts.token) params.token = opts.token;
  if (opts.limit !== undefined) params.limit = opts.limit;

  return await directApiInstance<ListUsersResponse>({
    url: '/sam/users',
    method: 'GET',
    params,
  });
};

/**
 * Search users by query string (name, email, username).
 *
 * SAM-213: this no longer swallows failures into an empty result. A genuine
 * backend failure now rejects so the caller can tell "search failed" apart from
 * "no matches" and surface a retryable error state. The request interceptor
 * already suppresses the global error toast for this endpoint (see
 * httpClient `isSilentEndpoint`), so rejecting here does not spam the user
 * while typing.
 */
export const searchUsers = async (
  query: string,
  limit: number = 60
) => {
  return await directApiInstance<ListUsersResponse>({
    url: '/sam/users/search',
    method: 'GET',
    params: { q: query, limit },
  });
};

/**
 * Create a new user and automatically assign to the creator's tenant
 */
export const createUser = async (
  userData: CreateUserRequest
) => {
  return await directApiInstance<CreateUserResponse>({
    url: '/sam/users',
    method: 'POST',
    data: userData,
    // SAM-1323: useCreateUser toasts a refused create itself; the generic toast would say it twice
    quietErrors: true,
  } as Parameters<typeof directApiInstance>[0]);
};

/**
 * Add a user to a Cognito group (business groups only)
 */
export const addUserToGroup = async (
  username: string,
  group: string,
  expectedSub?: string | null,
) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}/groups`,
    method: 'POST',
    data: { group },
    params: expectedSubParams(expectedSub),
  });
};

/**
 * Update a user's Cognito custom attributes (e.g. business_id).
 *
 * NOTE: requires the backend route `PUT /sam/users/{username}` in
 * samUserManagement (admin_update_user_attributes). Until that's deployed this
 * call returns 4xx/5xx and the caller surfaces the error.
 */
export const updateUserAttributes = async (
  username: string,
  attributes: { business_id?: string; [key: string]: any },
  expectedSub?: string | null,
) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}`,
    method: 'PUT',
    data: attributes,
    params: expectedSubParams(expectedSub),
  });
};

/**
 * Create a new Cognito group
 */
export const createGroup = async (name: string, description: string = '') => {
  return await directApiInstance<GenericMessageResponse>({
    url: '/sam/groups',
    method: 'POST',
    data: { name, description },
  });
};

/**
 * Remove a user from a group
 */
export const removeUserFromGroup = async (
  username: string,
  group: string,
  expectedSub?: string | null,
) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}/groups`,
    method: 'POST',
    data: { group, action: 'remove' },
    params: expectedSubParams(expectedSub),
  });
};

/**
 * List groups for a specific user
 */
export const getUserGroups = async (username: string) => {
  const response = await directApiInstance<{ groups: Array<{ name: string; description?: string }> }>({
    url: `/sam/users/${encodeURIComponent(username)}/groups`,
    method: 'GET',
  });
  return response;
};

/**
 * Reset a user's password (send temporary password email or set new password)
 */
export const resetPassword = async (
  username: string,
  options: ResetPasswordRequest,
  expectedSub?: string | null,
) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}/reset-password`,
    method: 'POST',
    data: options,
    params: expectedSubParams(expectedSub),
  });
};

/**
 * Change a user's password
 */
export const changePassword = async (
  username: string,
  newPassword: string,
  expectedSub?: string | null,
) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}/password`,
    method: 'PUT',
    data: { new_password: newPassword },
    params: expectedSubParams(expectedSub),
  });
};

/**
 * Disable a Cognito user
 */
export const disableUser = async (username: string, expectedSub?: string | null) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}/disable`,
    method: 'PUT',
    params: expectedSubParams(expectedSub),
  });
};

/**
 * Enable a Cognito user
 */
export const enableUser = async (username: string, expectedSub?: string | null) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}/enable`,
    method: 'PUT',
    params: expectedSubParams(expectedSub),
  });
};

/**
 * Delete a Cognito user (must be disabled first)
 */
export const deleteUser = async (username: string, expectedSub?: string | null) => {
  return await directApiInstance<GenericMessageResponse>({
    url: `/sam/users/${encodeURIComponent(username)}`,
    method: 'DELETE',
    params: expectedSubParams(expectedSub),
  });
};
